import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Loader2, Search, FileSignature, CheckCircle2, Clock } from 'lucide-react';
import { YourCheckaNavigation } from '@/components/Navbar';
import { Badge } from '@/components/ui/badge';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { useGetNotaryInvestigationsQuery } from '@/store/api/apiSlice';
import type { NotaryInvestigationListItem } from '@/types';

function getInitials(firstName?: string, lastName?: string) {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'NT';
}

export default function Notary() {
    const navigate = useNavigate();
    const currentUser = useAppSelector(selectCurrentUser);

    const { data: investigations = [], isLoading } = useGetNotaryInvestigationsQuery();
    const [searchQuery, setSearchQuery] = useState('');

    const filtered = useMemo(() => {
        if (!searchQuery.trim()) return investigations;
        const q = searchQuery.toLowerCase();
        return investigations.filter(
            (inv) =>
                (inv.employeeName ?? '').toLowerCase().includes(q) ||
                (inv.employerName ?? '').toLowerCase().includes(q),
        );
    }, [investigations, searchQuery]);

    return (
        <main className="w-full min-h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
            <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
                <YourCheckaNavigation
                    showTitle={false}
                    showInfo={false}
                    showLocation={false}
                    notificationCount={0}
                    userName={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Notary'}
                    userInitials={currentUser ? getInitials(currentUser.firstName, currentUser.lastName) : 'NT'}
                    only={true}
                    className="border-b border-gray-100"
                />

                <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:p-10 flex flex-col gap-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">
                                Notary Assignments
                            </h1>
                            <p className="text-sm text-gray-500 mt-1">
                                Review each employee's signed affidavit and upload the notarised copy.
                            </p>
                        </div>
                        <div className="relative w-full sm:max-w-xs">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search employee or employer"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-transparent"
                            />
                        </div>
                    </div>

                    <div className="rounded-2xl border border-gray-100 overflow-hidden">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-16">
                                <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className="flex flex-col items-center justify-center text-center py-12 px-6">
                                <p className="text-gray-500 text-sm">No investigations assigned to you yet.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[720px]">
                                    <thead className="bg-gray-50/60">
                                        <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            <th className="px-4 py-3">Employee</th>
                                            <th className="px-4 py-3">Employer</th>
                                            <th className="px-4 py-3">Affidavit</th>
                                            <th className="px-4 py-3">Notary status</th>
                                            <th className="px-4 py-3"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.map((inv) => (
                                            <NotaryRow
                                                key={inv.id}
                                                investigation={inv}
                                                onOpen={() => navigate(`/notary/${inv.id}`)}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
}

function NotaryRow({ investigation, onOpen }: { investigation: NotaryInvestigationListItem; onOpen: () => void }) {
    return (
        <tr onClick={onOpen} className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors cursor-pointer">
            <td className="px-4 py-4 text-sm font-medium text-gray-900">{investigation.employeeName || '—'}</td>
            <td className="px-4 py-4 text-sm text-gray-700">{investigation.employerName || '—'}</td>
            <td className="px-4 py-4">
                {investigation.isFormSubmitted ? (
                    <Badge
                        variant="outline"
                        className="bg-blue-50 text-blue-700 border-blue-100 px-3 py-1 rounded-lg font-medium w-fit"
                    >
                        Ready to review
                    </Badge>
                ) : (
                    <span className="inline-flex items-center gap-1.5 text-sm text-gray-400">
                        <Clock className="w-3.5 h-3.5" /> Awaiting employee
                    </span>
                )}
            </td>
            <td className="px-4 py-4">
                {investigation.notaryCompletedAt ? (
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-green-600">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Notarised {new Date(investigation.notaryCompletedAt).toLocaleDateString()}
                    </span>
                ) : (
                    <span className="text-sm text-amber-600 font-medium">Pending</span>
                )}
            </td>
            <td className="px-4 py-4 text-right">
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#FF6B35]">
                    <FileSignature className="w-4 h-4" /> Open
                </span>
            </td>
        </tr>
    );
}
